import { HttpError } from 'react-admin';
import type { DataProvider, GetListParams, GetOneParams } from 'react-admin';

import { requirePartner } from '@src/providers/dataProvider/shared';

import { fetchRedirects } from './fetchRedirects';
import { fetchRedirectGroup } from './fetchRedirectGroup';
import type { ShopifyProductRedirect, ShopifyProductRedirectEdge, ShopifyProductRedirectPageInfo } from './types';

interface ShopifyRedirectsDataProviderImpl {
  getList: (
    resource: string,
    params: GetListParams,
  ) => Promise<{
    data: ShopifyProductRedirect[];
    total: number | undefined;
    pageInfo: ShopifyProductRedirectPageInfo;
  }>;
  getOne: (
    resource: string,
    params: GetOneParams,
  ) => Promise<{
    data: ShopifyProductRedirect & {
      group: {
        currentTarget: string | null | undefined;
        redirects: ShopifyProductRedirect[];
        count: number;
      };
    };
  }>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: () => Promise<never>;
  update: () => Promise<never>;
  updateMany: () => Promise<never>;
  delete: () => Promise<never>;
  deleteMany: () => Promise<never>;
}

const impl: ShopifyRedirectsDataProviderImpl = {
  getList: async (_resource, params) => {
    try {
      const partner = requirePartner();
      const { page = 1, perPage = 50 } = params.pagination || {};
      const after = page > 1
        ? btoa(`cursor:${(page - 1) * perPage - 1}`)
        : null;

      const status = params.filter?.status as string | undefined;
      const handle = params.filter?.q as string | undefined;

      const response = await fetchRedirects({
        partnerCode: partner.code,
        first: perPage,
        after,
        status,
        handle,
      });

      const data = response.edges.map((edge: ShopifyProductRedirectEdge) => edge.node);

      return {
        data,
        total: response.totalCount ?? undefined,
        pageInfo: response.pageInfo,
      };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('shopifyRedirectsDataProvider.getList failed:', {
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load shopify redirects.', status);
    }
  },

  getOne: async (_resource, params) => {
    try {
      const partner = requirePartner();

      const group = await fetchRedirectGroup({
        partnerCode: partner.code,
        id: Number(params.id),
      });

      const primary = group.redirects?.find(
        (r: ShopifyProductRedirect | null | undefined) => r?.id === Number(params.id),
      );
      if (!primary) {
        throw new HttpError('Redirect not found', 404);
      }

      const filteredRedirects = (group.redirects ?? []).filter(
        (r: ShopifyProductRedirect | null | undefined): r is ShopifyProductRedirect => r != null,
      );

      return {
        data: {
          ...primary,
          group: {
            currentTarget: group.currentTarget,
            redirects: filteredRedirects,
            count: group.count ?? filteredRedirects.length,
          },
        },
      };
    } catch (error) {
      if (error instanceof HttpError) throw error;
      console.error('shopifyRedirectsDataProvider.getOne failed:', {
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load shopify redirect.', status);
    }
  },

  getMany: async () => {
    throw new Error('getMany not implemented for shopifyRedirects resource');
  },

  getManyReference: async () => {
    throw new Error('getManyReference not implemented for shopifyRedirects resource');
  },

  create: async () => {
    throw new Error('create not implemented for shopifyRedirects resource');
  },

  update: async () => {
    throw new Error('update not implemented for shopifyRedirects resource');
  },

  updateMany: async () => {
    throw new Error('updateMany not implemented for shopifyRedirects resource');
  },

  delete: async () => {
    throw new Error('delete not implemented for shopifyRedirects resource');
  },

  deleteMany: async () => {
    throw new Error('deleteMany not implemented for shopifyRedirects resource');
  },
};

export const shopifyRedirectsDataProvider = impl as unknown as DataProvider;

export default shopifyRedirectsDataProvider;
